// ============================================================================
// api_networkQuality.ts
// Estimation de la qualité du réseau montant, côté device — 4 niveaux Q1..Q4
// ============================================================================
//
// POURQUOI PAS LE SEUL DÉBIT. Un lien à 5 Mb/s qui perd 8 % des paquets n'est pas
// un bon lien : la vidéo y sera hachée malgré le débit. Le niveau combine donc
// trois mesures — débit disponible, taux de perte, temps d'aller-retour — et
// retient la PLUS MAUVAISE des trois. Un seul critère dégradé suffit à faire
// tomber le niveau, ce qui est le comportement honnête : c'est ce que verra la
// personne qui filme.
//
// POURQUOI DEUX JEUX DE SEUILS. Sans hystérésis, un débit qui oscille autour
// d'une frontière fait clignoter l'icône en permanence, et une icône qui
// clignote n'informe plus. Chaque niveau a donc un seuil d'ENTRÉE (exigeant) et
// un seuil de MAINTIEN (plus indulgent) : on monte difficilement, on reste
// facilement. La montée demande en plus plusieurs mesures consécutives, la
// descente est immédiate — une dégradation doit se voir tout de suite, une
// embellie peut attendre quelques secondes d'être confirmée.
//
// CE QUE ÇA NE MESURE PAS : le débit DESCENDANT. Le device publie, il ne reçoit
// que le retour programme. C'est bien la montée qui décide de la qualité de ce
// qu'il envoie à l'antenne.

import React from 'react';

export type NetworkQualityLevel = 1 | 2 | 3 | 4;

export interface NetworkSample {
  /** Débit montant estimé, en bits par seconde. */
  bitrate  : number;
  /** Part des paquets perdus sur l'intervalle, entre 0 et 1. */
  loss     : number;
  /** Aller-retour, en millisecondes. */
  rtt      : number;

  // ── Détail, pour la fenêtre d'informations ────────────────────────────────
  //
  // Ces champs ne participent PAS au classement : ils l'expliquent. Optionnels
  // pour que le classement reste testable avec une mesure réduite à ses trois
  // critères.

  /** D'où vient `bitrate` — l'honnêteté du chiffre en dépend. */
  bitrateSource?: 'estimation' | 'émis';
  /** Débit réellement émis sur l'intervalle, en bits/s. */
  sentBitrate?  : number;
  /** Gigue, en millisecondes. */
  jitter?       : number;
  /** Paquets émis sur l'intervalle. */
  packetsSent?  : number;
  /** Paquets perdus sur l'intervalle. */
  packetsLost?  : number;
  /** Retransmissions demandées par le récepteur, cumulées. */
  nackCount?    : number;
  /** Résolution et cadence réellement émises. */
  frameWidth?   : number;
  frameHeight?  : number;
  framesPerSecond?: number;
  /** Raison de limitation rapportée par l'encodeur : 'bandwidth', 'cpu', 'none'… */
  qualityLimitation?: string;
}

export interface NetworkQualityState {
  /** null tant qu'aucune mesure exploitable n'a été relevée. */
  level    : NetworkQualityLevel | null;
  sample   : NetworkSample | null;
  /** Mesures consécutives plaidant pour un niveau supérieur. */
  pending  : number;
}

export const initialNetworkQualityState: NetworkQualityState = {
  level: null, sample: null, pending: 0,
};

/**
 * Seuils d'ENTRÉE dans un niveau, et de MAINTIEN une fois dedans.
 *
 * Les valeurs d'entrée reprennent la grille WebRTC usuelle (3,5 / 1,5 / 0,5 Mb/s)
 * majorée d'environ 30 % : un débit mesuré à 3,5 Mb/s ne veut pas dire qu'on peut
 * durablement encoder à 3,5 Mb/s, et la marge évite d'annoncer une qualité que le
 * lien ne tiendra pas. Les valeurs de maintien sont la grille nue.
 */
interface Thresholds { bitrate: number; loss: number; rtt: number; }

const ENTER: Record<NetworkQualityLevel, Thresholds> = {
  4: { bitrate: 4_500_000, loss: 0.010, rtt: 100 },
  3: { bitrate: 2_000_000, loss: 0.030, rtt: 200 },
  2: { bitrate:   650_000, loss: 0.050, rtt: 400 },
  1: { bitrate:         0, loss: 1.000, rtt: Infinity },
};

const STAY: Record<NetworkQualityLevel, Thresholds> = {
  4: { bitrate: 3_500_000, loss: 0.015, rtt: 130 },
  3: { bitrate: 1_500_000, loss: 0.040, rtt: 260 },
  2: { bitrate:   500_000, loss: 0.065, rtt: 500 },
  1: { bitrate:         0, loss: 1.000, rtt: Infinity },
};

/** Mesures consécutives exigées avant de monter d'un niveau. */
export const UPGRADE_CONFIRMATIONS = 3;

const meets = (s: NetworkSample, t: Thresholds): boolean =>
  s.bitrate >= t.bitrate && s.loss <= t.loss && s.rtt <= t.rtt;

/** Le meilleur niveau que cette mesure permet d'ATTEINDRE. */
export const levelFromSample = (s: NetworkSample): NetworkQualityLevel => {
  if (meets(s, ENTER[4])) return 4;
  if (meets(s, ENTER[3])) return 3;
  if (meets(s, ENTER[2])) return 2;
  return 1;
};

/** Le niveau courant tient-il encore, aux seuils indulgents ? */
const holds = (s: NetworkSample, level: NetworkQualityLevel): boolean => meets(s, STAY[level]);

/**
 * Applique une mesure à l'état courant. Fonction PURE : tout le comportement
 * — hystérésis, confirmations, descente immédiate — se teste sans WebRTC.
 */
export const applySample = (
  state: NetworkQualityState,
  sample: NetworkSample,
): NetworkQualityState => {
  const candidate = levelFromSample(sample);

  // Première mesure : on prend le niveau tel quel, il n'y a rien à confirmer.
  if (state.level === null) {
    return { level: candidate, sample, pending: 0 };
  }

  // Descente : immédiate, dès que les seuils de maintien ne tiennent plus. Le
  // niveau retenu est celui que la mesure permet, pas « un cran en dessous » —
  // une coupure de débit doit se voir en entier.
  if (!holds(sample, state.level)) {
    return { level: candidate < state.level ? candidate : state.level, sample, pending: 0 };
  }

  // Montée : demandée par plusieurs mesures d'affilée, et d'UN cran à la fois.
  // Grimper de Q1 à Q4 d'un bond sur une accalmie passagère donnerait une icône
  // qui promet ce que le lien ne tiendra pas.
  if (candidate > state.level) {
    const pending = state.pending + 1;
    if (pending >= UPGRADE_CONFIRMATIONS) {
      return { level: (state.level + 1) as NetworkQualityLevel, sample, pending: 0 };
    }
    return { level: state.level, sample, pending };
  }

  return { level: state.level, sample, pending: 0 };
};

// ── Relevé des statistiques WebRTC ──────────────────────────────────────────

interface Cumulative { bytesSent: number; packetsSent: number; packetsLost: number; at: number; }

/**
 * Lit une PeerConnection et en tire une mesure, à partir du relevé précédent.
 *
 * Le débit vient d'`availableOutgoingBitrate` quand le navigateur le fournit —
 * c'est l'estimation du contrôle de congestion, donc ce qui est DISPONIBLE et non
 * ce qui est consommé. À défaut (Safari ne l'expose pas), on retombe sur le débit
 * réellement émis, calculé entre deux relevés : il minore le disponible, ce qui
 * fait sous-estimer la qualité plutôt que la sur-vendre.
 *
 * @returns null si la connexion ne dit encore rien d'exploitable.
 */
export const sampleNetworkQuality = async (
  pc: RTCPeerConnection,
  previous: React.MutableRefObject<Cumulative | null>,
): Promise<NetworkSample | null> => {
  if (pc.connectionState !== 'connected') return null;

  let report: RTCStatsReport;
  try { report = await pc.getStats(); }
  catch { return null; }

  let available = 0;
  let rtt       = 0;
  let jitter    = 0;
  let nackCount = 0;
  let frameWidth: number | undefined;
  let frameHeight: number | undefined;
  let framesPerSecond: number | undefined;
  let qualityLimitation: string | undefined;
  const now: Cumulative = { bytesSent: 0, packetsSent: 0, packetsLost: 0, at: Date.now() };

  report.forEach((r: any) => {
    // La paire de candidats retenue porte l'estimation de bande passante et le
    // temps d'aller-retour du chemin réellement emprunté.
    if (r.type === 'candidate-pair' && (r.nominated || r.state === 'succeeded')) {
      if (typeof r.availableOutgoingBitrate === 'number') available = r.availableOutgoingBitrate;
      if (typeof r.currentRoundTripTime === 'number')     rtt       = r.currentRoundTripTime * 1000;
    }
    if (r.type === 'outbound-rtp' && r.kind === 'video') {
      now.bytesSent   += r.bytesSent   ?? 0;
      now.packetsSent += r.packetsSent ?? 0;
      nackCount       += r.nackCount   ?? 0;
      // Résolution et cadence RÉELLEMENT émises : elles peuvent être bien en
      // dessous de celles de la caméra, l'encodeur ayant réduit de lui-même. Le
      // dire évite de chercher un défaut de caméra là où le réseau a tranché.
      if (typeof r.frameWidth      === 'number') frameWidth      = r.frameWidth;
      if (typeof r.frameHeight     === 'number') frameHeight     = r.frameHeight;
      if (typeof r.framesPerSecond === 'number') framesPerSecond = r.framesPerSecond;
      if (typeof r.qualityLimitationReason === 'string') qualityLimitation = r.qualityLimitationReason;
    }
    // Les pertes ne sont connues que du RÉCEPTEUR : elles reviennent par les
    // rapports RTCP, d'où « remote-inbound ». C'est aussi la seule source de RTT
    // quand la paire de candidats n'en donne pas.
    if (r.type === 'remote-inbound-rtp' && r.kind === 'video') {
      now.packetsLost += r.packetsLost ?? 0;
      if (typeof r.jitter === 'number') jitter = r.jitter * 1000;
      if (rtt === 0 && typeof r.roundTripTime === 'number') rtt = r.roundTripTime * 1000;
    }
  });

  const before = previous.current;
  previous.current = now;

  // Premier passage : rien à comparer, et `availableOutgoingBitrate` seul ne suffit
  // pas à juger des pertes. On attend le relevé suivant.
  if (!before) return null;

  const elapsedS = (now.at - before.at) / 1000;
  if (elapsedS <= 0) return null;

  const sentBitrate = ((now.bytesSent - before.bytesSent) * 8) / elapsedS;
  const deltaSent   = now.packetsSent - before.packetsSent;
  const deltaLost   = now.packetsLost - before.packetsLost;

  return {
    bitrate: available > 0 ? available : Math.max(0, sentBitrate),
    // Un delta négatif arrive après une republication (compteurs repartis de
    // zéro) : on le lit comme « aucune perte connue » plutôt que comme une
    // aberration qui ferait chuter le niveau sans raison.
    loss   : deltaSent > 0 && deltaLost > 0 ? Math.min(1, deltaLost / deltaSent) : 0,
    rtt    : rtt,

    bitrateSource: available > 0 ? 'estimation' : 'émis',
    sentBitrate  : Math.max(0, sentBitrate),
    jitter,
    packetsSent  : Math.max(0, deltaSent),
    packetsLost  : Math.max(0, deltaLost),
    nackCount,
    frameWidth, frameHeight, framesPerSecond, qualityLimitation,
  };
};

// ── Présentation ────────────────────────────────────────────────────────────

export const QUALITY_LABELS: Record<NetworkQualityLevel, string> = {
  1: 'Réseau très faible',
  2: 'Réseau faible',
  3: 'Réseau bon',
  4: 'Réseau excellent',
};

/** Résumé chiffré, pour l'infobulle : l'icône dit le niveau, pas le pourquoi. */
export const describeSample = (s: NetworkSample | null): string => {
  if (!s) return 'mesure en cours…';
  const mbps = (s.bitrate / 1_000_000).toFixed(s.bitrate < 1_000_000 ? 2 : 1);
  return `${mbps} Mb/s · ${(s.loss * 100).toFixed(1)} % de perte · ${Math.round(s.rtt)} ms`;
};
